import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppStore, AppDispatch, AppState } from '../../../store';
import { Box, Collapse, Alert, AlertTitle } from '@mui/material';
import { ErrorMessage } from '../../ErrorMessage';
import { EActionType, EAction, MarketFormFields, EDataTableType } from '../../../data/Constants';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import {
  getMarkets,
  updateMarket,
  createMarket,
  getMarketDetails,
} from '../../../store/states/MarketSlice';
import { resetAlertMessage, setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import { GenericSimpleForm } from '../../Layouts/GenericSimpleForm';
import { TFunction } from 'i18next';

import type { IAPIResponse, IError, IFormData, IManipulationProps } from '../../../../types';

//Call apis to create or update Markets
function callCreateOrUpdateApi(
  manipulationType: string,
  formData: IFormData,
  t: TFunction,
  dispatch: AppDispatch
) {
  delete formData['id'];
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateMarket({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        dispatch
      );
    });
  } else {
    dispatch(
      createMarket({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  }
}

function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
) {
  const markets = AppStore.getState().dataMaintainance.markets;
  if (!res || res.error) {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  } else {
    dispatch(
      getMarkets({
        page: Number(markets.currentPage),
        limit: Number(markets.recordsPerPage),
        searchCode: markets.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  }
}

const loadFormData = async (
  props: IManipulationProps,
  setInitialMarketData: React.Dispatch<React.SetStateAction<IFormData>>,
  dispatch: AppDispatch
) => {
  let marketDetails = await dispatch(getMarketDetails({ request: [props.id] })).unwrap();

  if (!('data' in marketDetails) || !marketDetails.data) {
    return;
  }
  const marketData = marketDetails.data[0];

  setInitialMarketData(marketData);
};

const setLocalAlertMessage = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
  if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
    setDisplayAlertMessage(true);
  }
};

export const MarketManipulationForm = (props: IManipulationProps) => {
  const { t } = useTranslation();
  const manipulationType = props.type; //To get the type of manipulation being performed
  const initialData = { code: '', name: '', description: '', isActive: false };

  const [initialMarketData, setInitialMarketData] = useState(initialData);
  const [displayAlertMessage, setDisplayAlertMessage] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector((state: AppState) => state.error);

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialMarketData, dispatch);
    }
  }, []);

  useEffect(() => {
    setLocalAlertMessage(error, setDisplayAlertMessage);
  }, [error]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(resetAlertMessage());
    dispatch(resetError());
    setDisplayAlertMessage(false);
  };

  const handleSubmit = (formData: IFormData) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, formData, t, dispatch);
  };

  return (
    <Box className="manipulationBox ">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <GenericSimpleForm
          formFields={MarketFormFields}
          initialData={initialMarketData}
          onSubmit={handleSubmit}
          manipulationType={manipulationType}
          setDisplayAlertMessage={setDisplayAlertMessage}
          type={EDataTableType.Markets}
          uniqueKey="code"
        />
      </Box>
    </Box>
  );
};
